import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ai } from "../lib/api";
import { Button } from "./ui/button";
import { Bot, Send, X, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function AiHelpAssistant() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  // Greet on first open.
  useEffect(() => {
    if (open && messages.length === 0) {
      const isEn = String(i18n.language || "").startsWith("en");
      setMessages([
        {
          role: "assistant",
          content: isEn
            ? "Hi! I'm the Canales Assistant. Ask me anything about this app — audits, checklists, findings, reports, users or notifications."
            : "¡Hola! Soy el Asistente Canales. Pregúntame lo que necesites sobre la app — auditorías, checklist, hallazgos, reportes, usuarios o notificaciones.",
        },
      ]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInput("");
    setBusy(true);
    try {
      const res = await ai.help(text, history);
      setMessages((prev) => [...prev, { role: "assistant", content: res.reply }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "AI service unavailable";
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            String(i18n.language || "").startsWith("en")
              ? `I couldn't reach the AI service right now. ${detail}`
              : `No pude contactar al servicio de IA ahora. ${detail}`,
          error: true,
        },
      ]);
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const isEn = String(i18n.language || "").startsWith("en");
  const title = isEn ? "Canales Assistant" : "Asistente Canales";
  const placeholder = isEn ? "Ask me anything about the app…" : "Pregúntame lo que sea de la app…";
  const tagline = isEn ? "Read-only Q&A — powered by Claude 4.5" : "Preguntas y respuestas — con Claude 4.5";

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={title}
          data-testid="ai-help-open"
          className="fixed bottom-5 right-5 z-40 group flex items-center gap-2 px-4 py-3 rounded-full bg-[#0B1220] text-white shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all border border-white/10"
        >
          <span className="relative grid place-items-center w-6 h-6 rounded-full bg-[#FACC15] text-[#0B1220]">
            <Bot size={14} strokeWidth={2.25} />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 ring-2 ring-[#0B1220]" />
          </span>
          <span className="text-[13px] font-semibold tracking-tight hidden sm:inline">
            {isEn ? "Ask Canales AI" : "Pregunta al AI"}
          </span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          role="dialog"
          aria-label={title}
          data-testid="ai-help-panel"
          className="fixed bottom-5 right-5 z-40 w-[min(92vw,380px)] h-[min(80vh,560px)] rounded-2xl overflow-hidden shadow-2xl border border-stone-200 bg-white flex flex-col animate-[fadeInUp_.18s_ease-out]"
          style={{ animation: "fadeInUp .18s ease-out" }}
        >
          {/* Header */}
          <div className="px-4 py-3 bg-[#0B1220] text-white flex items-center gap-2.5">
            <span className="relative grid place-items-center w-8 h-8 rounded-full bg-[#FACC15] text-[#0B1220]">
              <Bot size={16} strokeWidth={2.25} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm leading-tight truncate">{title}</div>
              <div className="text-[10.5px] text-stone-300 uppercase tracking-wider flex items-center gap-1 mt-0.5">
                <Sparkles size={9} /> {tagline}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              data-testid="ai-help-close"
              className="w-8 h-8 grid place-items-center rounded-full hover:bg-white/10 transition"
            >
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto bg-stone-50 px-3 py-3 space-y-2">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`msg-${m.role}-${i}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-[13.5px] leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#0B1220] text-white rounded-br-sm"
                      : m.error
                        ? "bg-red-50 text-red-700 border border-red-200 rounded-bl-sm"
                        : "bg-white text-stone-800 border border-stone-200 rounded-bl-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-stone-200 rounded-2xl rounded-bl-sm px-3 py-2 text-stone-500 text-sm flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> {isEn ? "Thinking…" : "Pensando…"}
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-stone-200 bg-white p-2.5">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                rows={1}
                className="flex-1 resize-none bg-stone-50 border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#0B1220] focus:ring-2 focus:ring-[#0B1220]/10 max-h-28"
                data-testid="ai-help-input"
                disabled={busy}
              />
              <Button
                type="button"
                onClick={send}
                disabled={busy || !input.trim()}
                className="h-10 w-10 p-0 rounded-full bg-[#0B1220] hover:bg-black text-white"
                data-testid="ai-help-send"
              >
                <Send size={16} />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Local keyframes (avoid touching global CSS) */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
      `}</style>
    </>
  );
}
